import { useCallback, useContext, useRef, useState } from 'react';
import { ToastComponent } from '@tylertech/forge';
import {
  getAllPrograms,
  getScoringPredictions,
} from '../services/programService.js';
import { getAllResultTypes, getScoringInfo } from '../services/resultsService.js';
import { useUser } from '../contexts/userContext.js';
import { useNameConventions } from '../contexts/nameConventionsContext.js';
import { LoaderContext } from '../contexts/loaderContext.js';
import { BPA, COMMUNITY, GOVERNANCE } from '../static/keyWords.js';
import { mapProgramsForPredictions } from '../components/DepartmentScoring/departmentScoringHelpers.js';
import { messageManager } from '../components/System/messageManager/messageManager.js';

const initialData = {
  programs: [],
  results: null,
  resultTypes: [],
};

const useDepartmentScoringData = () => {
  const [data, setData] = useState(initialData);
  const requestIdRef = useRef(0);
  const { user } = useUser();
  const { nameConventions } = useNameConventions();
  const { setLoading } = useContext(LoaderContext);

  const loadScoringData = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!user?.activeBudget?.id) {
      setData(initialData);
      setLoading(false);
      return false;
    }

    setLoading(true);
    setData(initialData);
    try {
      const [programsResponse, resultsResponse, resultTypesResponse] = await Promise.all([
        getAllPrograms(),
        getScoringInfo('Department'),
        getAllResultTypes(undefined, { disallowGlobalBadRequestPopups: true }),
      ]);

      const programs = programsResponse.data.filter((program) => program.serviceType?.scored);
      const results = resultsResponse.data;
      const resultTypes = resultTypesResponse.data;
      const usesRecommendations = resultTypes
        .filter(({ name }) => [BPA, COMMUNITY, GOVERNANCE].includes(name))
        .some(({ usingRecommendations }) => usingRecommendations);

      let programsWithPredictions = programs;
      if (usesRecommendations && programs.length > 0 && results?.[COMMUNITY] && results?.[GOVERNANCE]) {
        try {
          const predictionResponse = await getScoringPredictions({
            programs: mapProgramsForPredictions(programs, results),
          });
          const predictionsByProgram = new Map(
            predictionResponse.data.map(({ id, predictions }) => [id, predictions])
          );

          programsWithPredictions = programs.map((program) => ({
            ...program,
            suggestion: predictionsByProgram.get(program.id) ?? null,
          }));
        } catch {
          ToastComponent.present({
            message: messageManager(nameConventions).DEPARTMENT_SCORING.FETCH_PREDICTIONS_ERROR,
          });
        }
      }

      if (requestId === requestIdRef.current) {
        setData({ programs: programsWithPredictions, results, resultTypes });
      }
      return true;
    } catch {
      if (requestId === requestIdRef.current) {
        ToastComponent.present({ message: messageManager(nameConventions).DEPARTMENT_SCORING.FETCH_ERROR });
      }
      return false;
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [nameConventions, setLoading, user?.activeBudget?.id]);

  return {
    ...data,
    loadScoringData,
  };
};

export default useDepartmentScoringData;
